import React, { useEffect, useRef, useState } from 'react';
import { Alert, Box, Button, Chip, Skeleton, Typography } from '@mui/material';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import { HelpArticleDetail, friendlyAssistantError, getHelpArticle } from '../../../services/assistantCentral';
import AssistantMarkdown, { HELP_ARTICLE_HREF_PREFIX } from '../assistant/AssistantMarkdown';
import MessengerHeader from './MessengerHeader';
import { HelpArticleRow } from './MessengerHelp';
import { MESSENGER, messengerCardSx, messengerSubtitleSx } from './messengerTheme';

interface MessengerArticleProps {
  slug: string;
  onBack: () => void;
  onClose: () => void;
  onOpenArticle: (slug: string) => void;
  onNavigate: (href: string) => void;
  onAskAssistant: (question: string) => void;
}

const MessengerArticle: React.FC<MessengerArticleProps> = ({ slug, onBack, onClose, onOpenArticle, onNavigate, onAskAssistant }) => {
  const [article, setArticle] = useState<HelpArticleDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let alive = true;
    setArticle(null);
    setError(null);
    scrollRef.current?.scrollTo({ top: 0 });
    getHelpArticle(slug)
      .then((data) => alive && setArticle(data))
      .catch((e) => alive && setError(friendlyAssistantError(e, 'Não foi possível abrir este artigo.')));
    return () => {
      alive = false;
    };
  }, [slug]);

  const handleLink = (href: string) => {
    if (href.startsWith(HELP_ARTICLE_HREF_PREFIX)) onOpenArticle(href.slice(HELP_ARTICLE_HREF_PREFIX.length));
    else onNavigate(href);
  };

  return (
    <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', bgcolor: MESSENGER.surface }}>
      <MessengerHeader title={article?.collection_title || 'Ajuda'} onBack={onBack} onClose={onClose} />
      <Box ref={scrollRef} sx={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
        {error && (
          <Alert severity="warning" sx={{ m: 2, fontSize: '0.8rem' }}>
            {error}
          </Alert>
        )}
        {!article && !error && (
          <Box sx={{ p: 2.5 }}>
            <Skeleton width="80%" height={34} />
            <Skeleton width="40%" />
            {[0, 1, 2, 3, 4].map((i) => (
              <Skeleton key={i} />
            ))}
          </Box>
        )}
        {article && (
          <>
            <Box sx={{ px: 2.5, pt: 2.5, pb: 1 }}>
              <Typography component="h1" sx={{ fontWeight: 800, fontSize: '1.3rem', lineHeight: 1.25, color: MESSENGER.navy, letterSpacing: '-0.015em' }}>
                {article.title}
              </Typography>
              <Chip
                size="small"
                label={article.collection_title}
                sx={{ mt: 1, height: 22, fontSize: '0.7rem', fontWeight: 600, bgcolor: MESSENGER.inputBg, color: MESSENGER.slate }}
              />
            </Box>
            <Box sx={{ px: 2.5, pb: 2.5 }}>
              <AssistantMarkdown
                content={article.body}
                onNavigate={handleLink}
                sx={{
                  fontSize: '0.875rem',
                  lineHeight: 1.65,
                  color: MESSENGER.navy,
                  '& > *:not(:last-child)': { mb: 1.25 },
                }}
              />
            </Box>

            <Box sx={{ px: 2, pb: 2 }}>
              <Box sx={{ ...messengerCardSx, p: 2, textAlign: 'center', bgcolor: MESSENGER.canvas }}>
                <Typography sx={{ fontWeight: 700, fontSize: '0.88rem', color: MESSENGER.navy }}>Ainda precisa de ajuda?</Typography>
                <Typography sx={{ ...messengerSubtitleSx, mt: 0.25, mb: 1.25 }}>A IA analisa o seu caso e, se precisar, chama a equipe Klinvia.</Typography>
                <Button
                  variant="contained"
                  disableElevation
                  startIcon={<ChatBubbleOutlineIcon />}
                  onClick={() => onAskAssistant(`Li o artigo "${article.title}" e ainda tenho uma dúvida.`)}
                  sx={{
                    textTransform: 'none',
                    fontWeight: 700,
                    borderRadius: '10px',
                    bgcolor: MESSENGER.teal,
                    '&:hover': { bgcolor: MESSENGER.tealDark },
                  }}
                >
                  Perguntar à IA
                </Button>
              </Box>
            </Box>

            {article.related.length > 0 && (
              <Box sx={{ pb: 2 }}>
                <Typography sx={{ px: 2, pb: 0.5, fontWeight: 700, fontSize: '0.86rem', color: MESSENGER.navy }}>Artigos relacionados</Typography>
                {article.related.map((r) => (
                  <HelpArticleRow key={r.slug} article={r} onOpen={onOpenArticle} />
                ))}
              </Box>
            )}
          </>
        )}
      </Box>
    </Box>
  );
};

export default MessengerArticle;
